import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Plus, Send, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge, Button, Card, Dialog, Empty, ErrorNote, Field, Input, Loading, Select, Toggle } from '@/components/ui';
import { api } from '@/lib/api';
import { fmtDateTime, fmtWhen } from '@/lib/format';
import { rk, useAgents, useMe } from '@/lib/hooks';
import type { AlertEvent, AlertRule } from '@/lib/types';

const METRICS = {
  error_rate: { label: 'Error rate', unit: '%', hint: 'percent of runs that failed in the window' },
  daily_cost: { label: 'Spend (last 24h)', unit: 'USD', hint: 'needs model prices set under Models' },
  p95_latency: { label: 'p95 latency', unit: 'ms', hint: 'slowest 5% of runs in the window' },
} as const;

const fmt = (m: AlertRule['metric'], v: number | null) =>
  v === null ? '—' : m === 'error_rate' ? `${v.toFixed(1)}%` : m === 'daily_cost' ? `$${v.toFixed(2)}` : v >= 1000 ? `${(v / 1000).toFixed(1)}s` : `${Math.round(v)}ms`;

export const Alerts = () => {
  const me = useMe();
  const admin = me.data?.role === 'admin';
  const qc = useQueryClient();
  const agents = useAgents();
  const rules = useQuery({ queryKey: rk('alerts'), queryFn: () => api<{ rules: AlertRule[] }>('/v1/alerts'), refetchInterval: 30_000 });
  const events = useQuery({ queryKey: rk('alert-events'), queryFn: () => api<{ events: AlertEvent[] }>('/v1/alerts/events?limit=50'), refetchInterval: 30_000 });
  const [creating, setCreating] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: rk('alerts') });
    void qc.invalidateQueries({ queryKey: rk('alert-events') });
  };
  const toggle = useMutation({ mutationFn: (r: AlertRule) => api(`/v1/alerts/${r.id}`, { method: 'PATCH', body: { enabled: !r.enabled } }), onSuccess: refresh });
  const del = useMutation({ mutationFn: (id: string) => api(`/v1/alerts/${id}`, { method: 'DELETE' }), onSuccess: refresh });
  const test = useMutation({
    mutationFn: (id: string) => api<{ delivered: boolean; error: string | null }>(`/v1/alerts/${id}/test`, { body: {} }),
    onSuccess: (r) => (r.delivered ? toast.success('Test notification sent') : toast.error(`Not delivered: ${r.error}`)),
    onSettled: refresh,
  });
  const agentSlug = (id: string | null) => (id ? (agents.data?.agents.find((a) => a.id === id)?.slug ?? 'deleted agent') : 'All agents');

  return (
    <div className="flex flex-col gap-5">
      <Card
        title="Alert rules"
        actions={
          admin && (
            <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-3.5" /> New alert
            </Button>
          )
        }
      >
        {rules.isLoading ? (
          <Loading />
        ) : !rules.data?.rules.length ? (
          <Empty title="No alerts yet">Get a Slack message when an agent starts failing, gets slow, or spends more than expected. Checked every 5 minutes; you hear once when it fires and once when it recovers.</Empty>
        ) : (
          <ul>
            {rules.data.rules.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-3 border-b border-line-2 px-4 py-2.5 last:border-0">
                {r.state === 'firing' ? (
                  <Badge tone="bad">
                    <BellRing className="size-3" /> firing
                  </Badge>
                ) : (
                  <Badge tone="ok">ok</Badge>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-ink-3">
                    <span className="mono">{agentSlug(r.agentId)}</span> · {METRICS[r.metric].label} &gt; {fmt(r.metric, r.threshold)}
                    {r.metric !== 'daily_cost' && ` over ${r.windowMinutes} min (min ${r.minRequests} runs)`} · {r.channel} {r.target}
                  </div>
                </div>
                <div className="text-right text-xs text-ink-2 tabular-nums">
                  now {fmt(r.metric, r.lastValue)}
                  <div className="text-ink-3">checked {fmtWhen(r.lastEvaluatedAt)}</div>
                </div>
                {admin && (
                  <div className="flex items-center gap-1">
                    <Toggle checked={r.enabled} onChange={() => toggle.mutate(r)} label="" />
                    <Button size="sm" variant="ghost" loading={test.isPending && test.variables === r.id} onClick={() => test.mutate(r.id)} title="Send a test notification">
                      <Send className="size-3.5" />
                    </Button>
                    <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => confirm(`Delete "${r.name}"?`) && del.mutate(r.id)}>
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Recent notifications">
        {!events.data?.events.length ? (
          <p className="px-4 py-3 text-[13px] text-ink-3">Nothing yet.</p>
        ) : (
          <ul>
            {events.data.events.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-3 border-b border-line-2 px-4 py-2 text-[13px] last:border-0">
                <span className="w-36 text-xs text-ink-3">{fmtDateTime(e.at)}</span>
                <Badge tone={e.kind === 'firing' ? 'bad' : e.kind === 'resolved' ? 'ok' : 'neutral'}>{e.kind}</Badge>
                <span className="font-medium">{e.rule ?? 'deleted rule'}</span>
                {!e.delivered && <Badge tone="warn" title={e.error ?? ''}>not delivered</Badge>}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <NewAlert open={creating} onClose={() => setCreating(false)} onCreated={refresh} />
    </div>
  );
};

const NewAlert = ({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) => {
  const agents = useAgents();
  const [f, setF] = useState({ name: '', agentId: '', metric: 'error_rate' as AlertRule['metric'], threshold: '10', windowMinutes: '60', minRequests: '10', channel: 'slack' as AlertRule['channel'], target: '' });
  const create = useMutation({
    mutationFn: () =>
      api('/v1/alerts', {
        body: { ...f, agentId: f.agentId || null, threshold: Number(f.threshold), windowMinutes: Number(f.windowMinutes), minRequests: Number(f.minRequests) },
      }),
    onSuccess: () => {
      toast.success('Alert created — send a test to check the webhook');
      onCreated();
      onClose();
    },
  });
  const m = METRICS[f.metric];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New alert"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!f.name || !f.target} loading={create.isPending} onClick={() => create.mutate()}>
            Create
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Name">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Interviewer failing" autoFocus />
        </Field>
        <Field label="Agent">
          <Select value={f.agentId} onChange={(e) => setF({ ...f, agentId: e.target.value })}>
            <option value="">All agents</option>
            {(agents.data?.agents ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.slug}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="When" hint={m.hint}>
            <Select value={f.metric} onChange={(e) => setF({ ...f, metric: e.target.value as AlertRule['metric'], threshold: e.target.value === 'daily_cost' ? '20' : e.target.value === 'p95_latency' ? '15000' : '10' })}>
              {Object.entries(METRICS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={`Is above (${m.unit})`}>
            <Input type="number" min={0} value={f.threshold} onChange={(e) => setF({ ...f, threshold: e.target.value })} />
          </Field>
        </div>
        {f.metric !== 'daily_cost' && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Window (minutes)">
              <Input type="number" min={5} max={1440} value={f.windowMinutes} onChange={(e) => setF({ ...f, windowMinutes: e.target.value })} />
            </Field>
            <Field label="Only with at least (runs)">
              <Input type="number" min={1} value={f.minRequests} onChange={(e) => setF({ ...f, minRequests: e.target.value })} />
            </Field>
          </div>
        )}
        <div className="grid grid-cols-[120px_1fr] gap-3">
          <Field label="Send to">
            <Select value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value as AlertRule['channel'] })}>
              <option value="slack">Slack</option>
              <option value="webhook">Webhook</option>
            </Select>
          </Field>
          <Field label={f.channel === 'slack' ? 'Slack incoming webhook URL' : 'Webhook URL (receives JSON)'}>
            <Input value={f.target} onChange={(e) => setF({ ...f, target: e.target.value.trim() })} placeholder="https://hooks.slack.com/services/…" />
          </Field>
        </div>
        {create.error ? <ErrorNote error={create.error} /> : null}
      </div>
    </Dialog>
  );
};
